import { Select, Form as SemanticForm } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import audioBitrates from "astrid-distribution/src/constants/audioBitrates";
import audioFormats from "astrid-distribution/src/constants/audioFormats";
import { db, firebase } from "astrid-firebase";
import artifactValidationStatuses from "astrid-firestore/src/api/artifacts/constants/artifactValidationStatuses";
import audioSplit from "astrid-firestore/src/api/exports/constants/audioSplit";
import rename from "astrid-firestore/src/api/exports/constants/rename";
import createExport from "astrid-firestore/src/api/exports/createExport";
import createExportData from "astrid-firestore/src/api/exports/createExportData";
import useCollectionDataByIds from "astrid-firestore/src/hooks/useCollectionDataByIds";
import { boolean, enumValues, literal, object, string, union } from "astrid-firestore/src/types/_types";

import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";

import ArtifactValidatingMessage from "../../../artifacts/components/ArtifactValidatingMessage/ArtifactValidatingMessage";
import Form from "../../../forms/components/Form/Form";
import useSimpleForm from "../../../forms/hooks/useSimpleForm";

const audioFormatOptions = Object.values(audioFormats).map((audioFormat) => ({
	key: audioFormat,
	value: audioFormat,
	text: audioFormat,
}));

const bitrateOptions = Object.values(audioBitrates).map((bitrate) => ({
	key: bitrate,
	value: bitrate,
	text: bitrate,
}));

const splitOptions = Object.values(audioSplit).map((split) => ({
	key: split,
	value: split,
	text: split,
}));

const renameOptions = Object.values(rename).map((split) => ({
	key: split,
	value: split,
	text: split,
}));

const ExportFormValues = object({
	audioFormat: enumValues(audioFormats),
	bitrate: union(Object.values(audioBitrates).map(literal)),
	splitAudio: enumValues(audioSplit),
	isbn: string(),
	rename: enumValues(rename),
	zip: boolean(),
	includeCover: boolean(),
});

export default function CreateExportForm({ publisher, article, artifact, onClose }) {
	const { t } = useTranslation();
	const [selectedPreset, setSelectedPreset] = useState(null);

	const [exportPresets, loadingPresets, errorPresets] = useCollectionDataByIds(
		firebase,
		db.collection("exportPresets").where("deleted", "==", false),
		Object.keys(publisher?.exportPresets || {}),
	);

	const preset = selectedPreset ?? exportPresets?.[0]?.id ?? "custom";
	const isCustom = preset === "custom";

	const { form, onSubmit, error } = useSimpleForm({
		schema: article?.isbn
			? ExportFormValues
			: ExportFormValues.extend({
					isbn: string().trim().min(1, t("isbnOrTitleRequired", "ISBN or title is required")),
			  }),
		defaultValues: {
			audioFormat: audioFormats.WAV,
			bitrate: audioBitrates["192K"],
			splitAudio: audioSplit.AS_IS,
			isbn: article?.isbn || "",
			rename: rename.ISBN_SEQ,
			zip: false,
			includeCover: !!article?.cover,
		},
		onSubmit: (data) => {
			const theArticle = article?.isbn ? article : { isbn: data.isbn };
			if (!isCustom) {
				const presetData = exportPresets?.find((p) => p.id === preset);
				const exportDoc = createExportData(firebase, {
					article: theArticle,
					artifact,
					name: presetData.name,
					pipeline: presetData,
					publisher,
				});
				return exportDoc.ref.set(exportDoc);
			}
			return createExport(firebase, artifact, theArticle, { ...data, publisher });
		},
		onSuccess: onClose,
	});

	const { watch } = form;
	const audioFormat = watch("audioFormat");

	return (
		<Form.Modal
			size="tiny"
			header={t("createExport", "Create export")}
			form={form}
			error={error}
			onSubmit={onSubmit}
			onClose={onClose}
		>
			{artifact.status !== artifactValidationStatuses.VALID ? (
				<ArtifactValidatingMessage artifact={artifact} />
			) : (
				<LoadingContext loading={loadingPresets} error={errorPresets}>
					{exportPresets?.length > 0 && (
						<SemanticForm.Field>
							<label>{t("exportPreset", "Export preset")}</label>
							<Select
								options={[
									...(exportPresets?.map((p) => ({ key: p.id, value: p.id, text: p.name })) || []),
									{ key: "custom", value: "custom", text: t("custom", "Custom") },
								]}
								value={preset}
								onChange={(e, { value }) => setSelectedPreset(value)}
							/>
						</SemanticForm.Field>
					)}

					<Form.Input
						name="isbn"
						label={
							article?.isbn
								? t("isbn", "ISBN")
								: t("isbnOrTitle", "ISBN or title (used for naming of files/zip)")
						}
						disabled={!!article?.isbn}
					/>

					{isCustom && (
						<>
							<Form.Select
								name="audioFormat"
								label={t("audioFormat", "Audio format")}
								options={audioFormatOptions}
							/>
							{audioFormat === audioFormats.MP3 && (
								<Form.Select name="bitrate" label={t("bitrate", "Bitrate")} options={bitrateOptions} />
							)}
							<Form.Select
								name="splitAudio"
								label={t("splitAudio", "Split audio")}
								options={splitOptions}
							/>
							<Form.Select name="rename" label={t("nameAudio", "Name audio")} options={renameOptions} />
							<Form.Checkbox name="zip" label={t("zipAudio", "Zip audio")} />
							<Form.Checkbox
								name="includeCover"
								label={t("includeCover", "Include cover")}
								disabled={!article?.cover}
							/>
						</>
					)}
				</LoadingContext>
			)}
		</Form.Modal>
	);
}
